import { supabase } from './supabase/client';
import type {
  ParsedTransactionResult,
  ParseReceiptError,
  ParseReceiptErrorCode,
} from '@flows.cash/types';

// Vision + first-call schema compilation is slower than the text parser, and
// the request body carries a few hundred KB of image.
const CLIENT_TIMEOUT_MS = 60_000;

export type ParseReceiptResponse =
  | { data: ParsedTransactionResult; error: null }
  | { data: null; error: ParseReceiptError };

function errorResponse(code: ParseReceiptErrorCode): ParseReceiptResponse {
  return { data: null, error: { error: code } };
}

/**
 * Sends a compressed receipt/screenshot image (base64, no data-URI prefix) to
 * the parse-receipt Edge Function and returns structured fields for
 * pre-filling the add form — the same shape as the text parser, so callers
 * reuse the existing review flow. Never throws — all failures resolve to a
 * ParseReceiptError so callers can fall back to manual entry.
 */
export async function parseReceiptImage(
  imageBase64: string,
  mediaType: 'image/jpeg' | 'image/png' | 'image/webp' = 'image/jpeg'
): Promise<ParseReceiptResponse> {
  if (!imageBase64) return errorResponse('parse_unavailable');

  // The user's local date, so undated receipts land on their "today".
  const now = new Date();
  const localDate = [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, '0'),
    String(now.getDate()).padStart(2, '0'),
  ].join('-');

  const timeout = new Promise<ParseReceiptResponse>((resolve) =>
    setTimeout(() => resolve(errorResponse('parse_unavailable')), CLIENT_TIMEOUT_MS)
  );

  const request = (async (): Promise<ParseReceiptResponse> => {
    try {
      const { data, error } = await supabase.functions.invoke('parse-receipt', {
        body: { imageBase64, mediaType, localDate },
      });

      if (error) {
        // FunctionsHttpError carries the original Response as `context`.
        const status: number | undefined = (error as any)?.context?.status;
        if (status === 429) return errorResponse('rate_limited');
        if (status === 413) return errorResponse('image_too_large');
        if (status === 422) return errorResponse('not_a_receipt');
        if (status === 400) return errorResponse('unsupported_image');
        return errorResponse('parse_unavailable');
      }

      if (typeof data?.amount !== 'number' || typeof data?.type !== 'string') {
        return errorResponse('parse_unavailable');
      }

      return { data: data as ParsedTransactionResult, error: null };
    } catch (err) {
      console.error('parseReceiptImage failed:', err);
      return errorResponse('parse_unavailable');
    }
  })();

  return Promise.race([request, timeout]);
}
